import { useEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'wouter';
import { buildAdminTournamentPath, parseAdminTournamentIdFromPath } from '@shared/tournamentUrls';
import {
  Home,
  Search,
  Heart,
  Settings,
  Calendar,
  MessageSquare,
  LayoutDashboard,
  Users,
  FileText,
  Grid3X3,
  Warehouse,
  Target,
  Gamepad2,
  Shirt,
  BarChart3,
  Play,
  Eye,
  CloudRain,
  MoreHorizontal,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import type { CSSProperties } from 'react';
import type { NavContext } from '@/hooks/use-nav-context';
import { track } from '@/lib/posthog';
import { useVisualViewportBottomOffset } from '@/hooks/use-visual-viewport-bottom-offset';
import { useQuery } from '@tanstack/react-query';
import { getPublicTabs } from '@/lib/publicTournamentTabs';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';

interface NavItem {
  id: string;
  label: string;
  icon: typeof Home;
  href: string;
  testId: string;
  disabled?: boolean;
  comingSoon?: boolean;
  /** Opens More sheet instead of navigating (Field Command). */
  opensMore?: boolean;
}

interface UnifiedMobileNavProps {
  context: NavContext;
  tournamentId?: string | null;
  organizationId?: string | null;
  coachTeamToken?: string | null;
  isAuthenticated: boolean;
  primaryColor?: string | null;
  onChatOpen?: () => void;
}

export function UnifiedMobileNav({
  context,
  tournamentId,
  organizationId,
  coachTeamToken,
  isAuthenticated,
  primaryColor,
  onChatOpen,
}: UnifiedMobileNavProps) {
  const [location, setLocation] = useLocation();

  const adminTournamentIdFromPath = parseAdminTournamentIdFromPath(location);
  const adminTournamentId = tournamentId || adminTournamentIdFromPath;

  // Fetch tournament for primary color (public) or Bracket/Pools nav label (admin)
  const { data: tournament } = useQuery<{
    primaryColor?: string | null;
    type?: string | null;
  }>({
    queryKey: ['/api/tournaments', context === 'admin' ? adminTournamentId : tournamentId],
    enabled:
      (context === 'tournament-public' && !!tournamentId) ||
      (context === 'admin' && !!adminTournamentId),
  });

  const effectivePrimaryColor = tournament?.primaryColor || primaryColor || '#22c55e';
  const poolsOrBracketLabel = tournament?.type === 'double_elimination' ? 'Bracket' : 'Pools';
  const [moreOpen, setMoreOpen] = useState(false);

  const { data: adminGames = [] } = useQuery<unknown[]>({
    queryKey: ['/api/tournaments', adminTournamentId, 'games'],
    enabled: context === 'admin' && !!adminTournamentId,
    staleTime: 60_000,
  });
  const fieldCommandActive = context === 'admin' && !!adminTournamentId && adminGames.length > 0;

  // Get navigation items based on context
  const getNavItems = (): NavItem[] => {
    switch (context) {
      case 'tournament-public': {
        // Extract tournamentId from location if not provided
        let effectiveTournamentId = tournamentId;
        if (!effectiveTournamentId) {
          const match = location.match(/^\/tournament\/([^/?]+)/);
          if (match) {
            effectiveTournamentId = match[1];
          }
        }

        if (!effectiveTournamentId) {
          // Debug: log why we can't get tournamentId
          if (process.env.NODE_ENV === 'development') {
            console.warn(
              'UnifiedMobileNav: tournament-public context but no tournamentId. Location:',
              location
            );
          }
          return [];
        }

        // Keep in sync with getPublicTabs() in `lib/publicTournamentTabs.ts`.
        const publicTabs = getPublicTabs(tournament?.type);
        return [
          ...publicTabs.map(tab => ({
            id: tab.id,
            label: tab.label,
            icon: tab.icon,
            href: `/tournament/${effectiveTournamentId}?tab=${tab.id}`,
            testId: `nav-${tab.id}`,
          })),
          {
            id: 'chat',
            label: 'Chat',
            icon: MessageSquare,
            href: '#',
            testId: 'nav-chat',
            disabled: true,
            comingSoon: true,
          },
        ];
      }

      case 'admin': {
        // Extract organizationId from location if not provided
        let effectiveOrgId = organizationId;
        if (!effectiveOrgId) {
          const orgMatch = location.match(/^\/org\/([^/?]+)/);
          if (orgMatch) {
            effectiveOrgId = orgMatch[1];
          }
        }

        // Extract tournamentId from location if not provided
        let effectiveAdminTournamentId = tournamentId;
        if (!effectiveAdminTournamentId) {
          effectiveAdminTournamentId = parseAdminTournamentIdFromPath(location);
        }

        if (!effectiveOrgId) {
          if (process.env.NODE_ENV === 'development') {
            console.warn(
              'UnifiedMobileNav: admin context but no organizationId. Location:',
              location
            );
          }
          return [];
        }

        if (effectiveAdminTournamentId) {
          const path = (tab: string) =>
            buildAdminTournamentPath(effectiveOrgId, effectiveAdminTournamentId, { tab });

          // Field Command: day-of radio (Live | Games | Weather | More)
          if (fieldCommandActive) {
            return [
              {
                id: 'overview',
                label: 'Live',
                icon: Eye,
                href: path('overview'),
                testId: 'nav-overview',
              },
              {
                id: 'games',
                label: 'Games',
                icon: Gamepad2,
                href: path('games'),
                testId: 'nav-games',
              },
              {
                id: 'weather',
                label: 'Weather',
                icon: CloudRain,
                href: `${path('overview')}&mission=rain`,
                testId: 'nav-weather',
              },
              {
                id: 'more',
                label: 'More',
                icon: MoreHorizontal,
                href: '#',
                testId: 'nav-more',
                opensMore: true,
              },
            ];
          }

          // Setup rail: fuller station list (+ Tournaments escape hatch). No separate Allocations.
          return [
            {
              id: 'overview',
              label: 'Live',
              icon: Eye,
              href: path('overview'),
              testId: 'nav-overview',
            },
            {
              id: 'dashboard',
              label: 'Tournaments',
              icon: LayoutDashboard,
              href: `/org/${effectiveOrgId}/tournaments`,
              testId: 'nav-tournaments',
            },
            {
              id: 'communications',
              label: 'Messages',
              icon: MessageSquare,
              href: path('communications'),
              testId: 'nav-communications',
            },
            {
              id: 'teams',
              label: 'Teams',
              icon: Users,
              href: path('teams'),
              testId: 'nav-teams',
            },
            {
              id: 'pools',
              label: poolsOrBracketLabel,
              icon: Grid3X3,
              href: path('pools'),
              testId: 'nav-pools',
            },
            {
              id: 'facility',
              label: 'Facility',
              icon: Warehouse,
              href: path('facility'),
              testId: 'nav-facility',
            },
            {
              id: 'schedule',
              label: 'Schedule',
              icon: Calendar,
              href: path('schedule'),
              testId: 'nav-schedule',
            },
            {
              id: 'games',
              label: 'Games',
              icon: Gamepad2,
              href: path('games'),
              testId: 'nav-games',
            },
            {
              id: 'playoffs',
              label: 'Playoffs',
              icon: Target,
              href: path('playoffs'),
              testId: 'nav-playoffs',
            },
            {
              id: 'reports',
              label: 'Reports',
              icon: FileText,
              href: path('reports'),
              testId: 'nav-reports',
            },
          ];
        } else {
          // Org-level admin nav - show more options
          const isForestGlade =
            effectiveOrgId === '7940b20e-9d00-4728-b40f-335139f8f3b2' ||
            effectiveOrgId === 'forest-glade-baseball';

          const items: NavItem[] = [
            {
              id: 'tournaments',
              label: 'Tournaments',
              icon: LayoutDashboard,
              href: `/org/${effectiveOrgId}/tournaments`,
              testId: 'nav-tournaments',
            },
          ];

          // League Mgmt for Forest Glade — Command Center + pillar shortcuts (any org admin screen)
          if (isForestGlade) {
            items.unshift(
              {
                id: 'league-manifest',
                label: 'Uniforms',
                icon: Shirt,
                href: '/admin/league/uniforms',
                testId: 'nav-league-manifest',
              },
              {
                id: 'league-schedule',
                label: 'Scheduler',
                icon: Calendar,
                href: '/admin/league/master-scheduler',
                testId: 'nav-league-scheduler',
              },
              {
                id: 'league',
                label: 'League',
                icon: LayoutDashboard,
                href: '/admin/league',
                testId: 'nav-league',
              }
            );
          }

          items.push({
            id: 'settings',
            label: 'Settings',
            icon: Settings,
            href: `/org/${effectiveOrgId}/settings`,
            testId: 'nav-settings',
          });

          return items;
        }
      }

      case 'coach': {
        // Coach POV: operational tool. Keep nav minimal and task-oriented.
        // If we have a coach team token, provide a guaranteed way home.
        const items: NavItem[] = [];

        if (coachTeamToken) {
          items.push({
            id: 'coach-home',
            label: 'My Team',
            icon: Home,
            href: `/coach/${coachTeamToken}`,
            testId: 'nav-coach-home',
          });
        }

        // Provide a quick jump to the public broadcast scoreboard (if tournament is known)
        if (tournamentId) {
          items.push({
            id: 'coach-scoreboard',
            label: 'Scoreboard',
            icon: LayoutDashboard,
            href: `/tournament/${tournamentId}?tab=scoreboard`,
            testId: 'nav-coach-scoreboard',
          });
        }

        return items;
      }

      case 'public': {
        const items: NavItem[] = [
          {
            id: 'home',
            label: 'Home',
            icon: Home,
            href: '/',
            testId: 'nav-home',
          },
          {
            id: 'search',
            label: 'Search',
            icon: Search,
            href: '/directory',
            testId: 'nav-search',
          },
        ];

        if (isAuthenticated) {
          items.push(
            {
              id: 'favorites',
              label: 'My Team',
              icon: Heart,
              href: '/directory?favorites=true',
              testId: 'nav-favorites',
            },
            {
              id: 'settings',
              label: 'Settings',
              icon: Settings,
              href: '/directory?settings=true',
              testId: 'nav-settings',
            }
          );
        }

        return items;
      }

      default:
        return [];
    }
  };

  const navItems = getNavItems();
  const itemRefs = useRef<Map<string, HTMLElement>>(new Map());
  const navRef = useRef<HTMLElement | null>(null);
  const visualBottomOffset = useVisualViewportBottomOffset();

  // Tap-flash state: drives a brief amber ring on the tapped tab so the user
  // gets instant visual confirmation even if the route resolution lags.
  // Cleared after ~220ms; combines with `active:scale-95` and the haptic.
  const [flashId, setFlashId] = useState<string | null>(null);

  // Helper function to check if item is active
  const isActive = (item: NavItem): boolean => {
    if (item.disabled) {
      return false;
    }

    if (item.id === 'chat') {
      return false; // Chat is handled via onClick
    }

    // For tab-based navigation, check URL params
    if (item.href.includes('?tab=')) {
      const urlParams = new URLSearchParams(window.location.search);
      const tabParam = urlParams.get('tab');
      const itemTab = item.href.match(/[?&]tab=([^&]+)/)?.[1];
      if (tabParam && itemTab) {
        return tabParam === itemTab;
      }
      // If no tab param but we're on the base path, check if this is the default tab
      const basePath = item.href.split('?')[0];
      if (location.startsWith(basePath) && !tabParam && item.id === 'scoreboard') {
        return true; // Scoreboard is the default tab when ?tab= is missing.
      }
    }

    // For exact path matches
    if (item.href === location || item.href === '#') {
      return false; // Don't highlight chat or hash links
    }

    // For path prefix matches (excluding query params)
    const currentPath = location.split('?')[0];
    const itemPath = item.href.split('?')[0];
    return currentPath === itemPath;
  };

  // Scroll active tab into view when location changes — immediate (no
  // setTimeout / smooth) so the bottom-bar centering happens on the same
  // frame as the active state change. The 150ms smooth-scroll previously
  // here competed with the page-level smooth scroll and felt sluggish.
  useEffect(() => {
    const activeItem = navItems.find(item => isActive(item));
    if (!activeItem) return;
    const element = itemRefs.current.get(activeItem.testId);
    element?.scrollIntoView({ behavior: 'auto', block: 'nearest', inline: 'center' });
  }, [location, navItems, isActive]);

  const hideNavOnDesktop = context === 'admin' ? 'lg:hidden' : 'md-tall:hidden';

  if (navItems.length === 0) {
    // If no items, still render a minimal nav to help debug
    if (process.env.NODE_ENV === 'development') {
      return (
        <nav
          className={cn(
            'fixed bottom-0 left-0 right-0 z-50',
            hideNavOnDesktop,
            'border-t border-red-500 border-2',
            'bg-red-500/20'
          )}
          style={{
            paddingBottom: 'env(safe-area-inset-bottom, 0px)',
          }}
          data-testid="unified-mobile-nav-debug"
        >
          <div className="flex items-center justify-center h-16 text-white text-xs p-2">
            Debug: No nav items (context: {context})
          </div>
        </nav>
      );
    }
    return null;
  }

  const handleItemClick = (item: NavItem, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (item.disabled) {
      return;
    }

    // Instant tap-flash so the user gets visual confirmation on the same
    // frame as the tap, regardless of how long route resolution takes.
    setFlashId(item.testId);
    window.setTimeout(() => {
      setFlashId(current => (current === item.testId ? null : current));
    }, 220);

    if (item.id === 'chat' && onChatOpen) {
      onChatOpen();
      return;
    }

    if (item.opensMore) {
      setMoreOpen(true);
      if ('vibrate' in navigator) navigator.vibrate(10);
      return;
    }

    const url = new URL(item.href, window.location.origin);
    const newPath = url.pathname + url.search;

    if (context === 'tournament-public' && item.href.includes('?tab=')) {
      const itemTab = item.href.match(/[?&]tab=([^&]+)/)?.[1];
      let effectiveTournamentId = tournamentId;
      if (!effectiveTournamentId) {
        const match = location.match(/^\/tournament\/([^/?]+)/);
        if (match) effectiveTournamentId = match[1];
      }
      if (itemTab && effectiveTournamentId) {
        track('public_tab_clicked', { tabName: itemTab, tournamentId: effectiveTournamentId });
      }
    }

    window.history.pushState({}, '', newPath);
    setLocation(url.pathname + url.search);

    // Tournament dashboard listens for `locationchange`; dispatching it here
    // keeps the URL-only push in sync with subscribers.
    window.dispatchEvent(new Event('locationchange'));

    // Page scroll position is intentionally preserved here; the page-level
    // auto-scroll-into-view of the new tab content lives in
    // `tournament-dashboard.tsx` so it fires regardless of how the active
    // tab changed (bottom nav, deep link, popstate, etc.).

    if ('vibrate' in navigator) {
      navigator.vibrate(10);
    }
  };

  const moreOrgId = organizationId || location.match(/^\/org\/([^/?]+)/)?.[1] || null;
  const moreStations: NavItem[] =
    fieldCommandActive && moreOrgId && adminTournamentId
      ? [
          ...(
            [
              ['communications', 'Messages', MessageSquare],
              ['teams', 'Teams', Users],
              ['pools', poolsOrBracketLabel, Grid3X3],
              ['facility', 'Facility', Warehouse],
              ['schedule', 'Schedule', Calendar],
              ['playoffs', 'Playoffs', Target],
              ['reports', 'Reports', FileText],
            ] as Array<[string, string, typeof Home]>
          ).map(([id, label, icon]) => ({
            id,
            label,
            icon,
            href: buildAdminTournamentPath(moreOrgId, adminTournamentId, { tab: id }),
            testId: `more-${id}`,
          })),
          {
            id: 'dashboard',
            label: 'Tournaments',
            icon: LayoutDashboard,
            href: `/org/${moreOrgId}/tournaments`,
            testId: 'more-tournaments',
          },
        ]
      : [];

  return createPortal(
    <>
      <Sheet open={moreOpen} onOpenChange={setMoreOpen}>
        <SheetContent
          side="bottom"
          className="rounded-t-xl border-white/10 bg-[#020617] text-sport-text pb-safe max-h-[75dvh]"
          data-testid="field-command-more-sheet"
        >
          <SheetHeader>
            <SheetTitle className="font-roboto-condensed-bold uppercase tracking-wider text-championship-gold">
              More stations
            </SheetTitle>
          </SheetHeader>
          <div className="mt-4 grid grid-cols-2 gap-2">
            {moreStations.map(station => {
              const Icon = station.icon;
              return (
                <button
                  key={station.testId}
                  type="button"
                  className="flex min-h-14 items-center gap-3 rounded-lg border border-white/10 bg-black/40 px-3 py-3 text-left touch-manipulation active:scale-[0.98]"
                  data-testid={station.testId}
                  onClick={e => {
                    setMoreOpen(false);
                    handleItemClick(station, e);
                  }}
                >
                  <Icon className="h-5 w-5 text-championship-gold shrink-0" />
                  <span className="font-broadcast text-sm uppercase tracking-wider">
                    {station.label}
                  </span>
                </button>
              );
            })}
          </div>
        </SheetContent>
      </Sheet>
      <nav
        ref={navRef}
        className={cn(
          'fixed bottom-0 left-0 right-0 z-50',
          hideNavOnDesktop, // Admin: show through 1024px; public: hide on md-tall+
          'border-t border-white/10', // ESPN PRO: Machined separator
          'shadow-[0_-4px_20px_rgba(0,0,0,0.4)]',
          'bg-slate-900/60 backdrop-blur-md'
        )}
        style={
          {
            '--brand-primary': effectivePrimaryColor,
            paddingBottom: 'max(env(safe-area-inset-bottom, 0px), 0px)',
            position: 'fixed',
            bottom: visualBottomOffset,
            left: 0,
            right: 0,
            zIndex: 50,
            transform: 'translateZ(0)',
          } as CSSProperties
        }
        data-testid="unified-mobile-nav"
      >
        <div
          className={cn(
            'flex items-center h-16 min-h-[64px]',
            'overflow-x-auto scrollbar-hide',
            'snap-x snap-mandatory',
            'scroll-smooth'
          )}
          style={{
            WebkitOverflowScrolling: 'touch',
          }}
        >
          {navItems.map(item => {
            const active = isActive(item);
            const flashing = flashId === item.testId;
            const Icon = item.icon;

            return (
              <button
                key={item.testId}
                type="button"
                onClick={e => {
                  handleItemClick(item, e);
                }}
                ref={el => {
                  if (el) {
                    itemRefs.current.set(item.testId, el);
                  } else {
                    itemRefs.current.delete(item.testId);
                  }
                }}
                className={cn(
                  'flex flex-col items-center justify-center flex-shrink-0 h-full px-3 min-w-[80px]',
                  'transition-all duration-200 active:scale-95 touch-manipulation',
                  'snap-start',
                  'relative group',
                  item.disabled ? 'cursor-not-allowed opacity-45 grayscale' : 'cursor-pointer',
                  active
                    ? 'text-white'
                    : item.disabled
                      ? 'text-slate-600'
                      : 'text-slate-400 hover:text-slate-200',
                  flashing && 'ring-2 ring-championship-gold/70 ring-offset-0 brightness-125'
                )}
                disabled={item.disabled}
                aria-disabled={item.disabled}
                aria-pressed={!item.disabled && active}
                data-testid={item.testId}
              >
                {/* Active indicator bar with enhanced visibility */}
                {active && (
                  <div className="absolute top-0 left-0 right-0 h-0.5 rounded-b-full bg-championship-gold shadow-[0_0_12px_rgba(212,175,55,0.5)]" />
                )}

                {/* Background glow for active state with stronger visibility */}
                {active && (
                  <div className="absolute inset-0 rounded-t-lg -z-10 transition-all duration-300 bg-championship-gold/12 shadow-[inset_0_0_20px_rgba(212,175,55,0.1)]" />
                )}

                {/* Icon with enhanced active state */}
                <Icon
                  className={cn(
                    'h-6 w-6 mb-0.5 transition-all duration-300',
                    active
                      ? 'scale-110 drop-shadow-lg text-championship-gold drop-shadow-[0_0_4px_rgba(212,175,55,0.5)]'
                      : 'scale-100'
                  )}
                  style={{
                    strokeWidth: active ? 2.5 : 2,
                  }}
                />

                {/* Label - ESPN PRO: Broadcast font */}
                <span
                  className={cn(
                    'text-[10px] leading-tight transition-colors font-broadcast-normal tracking-wider',
                    active ? 'font-semibold' : 'font-normal'
                  )}
                >
                  {item.label.toUpperCase()}
                </span>
                {item.comingSoon && (
                  <span className="mt-0.5 rounded-full border border-white/10 bg-slate-800/80 px-1.5 py-0.5 text-[8px] leading-none font-broadcast-normal tracking-wider text-slate-400">
                    SOON
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </nav>
    </>,
    document.body
  );
}
